/**
 * External Game Service
 * 
 * Handles navigation and analytics for external games that open
 * in new windows instead of iframes (e.g., imaginesports.com)
 */

import posthog from 'posthog-js';
import type { Game } from '@/types/game';

// Configuration for external game handling
const EXTERNAL_CONFIG = {
  // Whitelist of allowed external domains
  allowedDomains: [
    'imaginesports.com',
    'www.imaginesports.com'
  ],

  // Analytics event names
  events: {
    externalGameClick: 'external_game_click',
    externalGameBlocked: 'external_game_blocked',
    externalGameValidated: 'external_game_validated'
  }
} as const;

/**
 * Validates if a URL is allowed for external game navigation
 */
export function validateExternalUrl(url: string): boolean {
  if (!url) return false;
  
  try {
    const parsedUrl = new URL(url);
    
    // Check if domain is in whitelist
    const isAllowed = EXTERNAL_CONFIG.allowedDomains.some(domain => 
      parsedUrl.hostname === domain || parsedUrl.hostname.endsWith(`.${domain}`)
    );
    
    // Must be HTTPS in production
    const isSecure = parsedUrl.protocol === 'https:' || 
      (process.env.NODE_ENV === 'development' && parsedUrl.protocol === 'http:');
    
    return isAllowed && isSecure;
  } catch (error) {
    console.error('Invalid external URL:', url, error);
    return false;
  }
}

/**
 * Detects if a game is an external game type
 */
export function isExternalGame(game: Game): boolean {
  // External games are identified by challengeType being 'external'
  return game.challengeType === 'external';
}

/**
 * Gets the external URL for a game
 */
export function getExternalGameUrl(game: Game): string | null {
  if (!isExternalGame(game)) return null;
  
  // For external games, use the frontend field in urlPattern
  const externalUrl = game.urlPattern?.frontend as string;
  
  if (!externalUrl) {
    console.warn('External game has no URL configured:', game.id);
    return null;
  }
  
  return externalUrl;
}

/**
 * Opens an external game in a new window with security measures
 */
export function openExternalGame(game: Game): boolean {
  const externalUrl = getExternalGameUrl(game);
  
  if (!externalUrl) {
    console.error('Cannot open external game: No URL configured');
    trackExternalGameEvent(EXTERNAL_CONFIG.events.externalGameBlocked, game, {
      reason: 'no_url_configured'
    });
    return false;
  }
  
  if (!validateExternalUrl(externalUrl)) {
    console.error('Cannot open external game: URL validation failed', externalUrl);
    trackExternalGameEvent(EXTERNAL_CONFIG.events.externalGameBlocked, game, {
      reason: 'url_validation_failed',
      url: externalUrl
    });
    return false;
  }
  
  try {
    // Track successful validation
    trackExternalGameEvent(EXTERNAL_CONFIG.events.externalGameValidated, game, {
      url: externalUrl
    });
    
    // Open external game in new tab (using link click for better browser compatibility)
    const link = document.createElement('a');
    link.href = externalUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    
    // Track successful external game launch
    trackExternalGameEvent(EXTERNAL_CONFIG.events.externalGameClick, game, {
      url: externalUrl,
      success: true
    });
    
    return true;
  } catch (error) {
    console.error('Error opening external game:', error);
    trackExternalGameEvent(EXTERNAL_CONFIG.events.externalGameBlocked, game, {
      reason: 'window_open_error',
      error: error instanceof Error ? error.message : String(error),
      url: externalUrl
    });
    return false;
  }
}

/**
 * Tracks analytics events for external game interactions
 */
function trackExternalGameEvent(eventName: string, game: Game, additionalProps: Record<string, unknown> = {}): void {
  try {
    posthog.capture(eventName, {
      game_id: game.id,
      game_name: game.displayName,
      challenge_type: game.challengeType,
      is_external: true,
      timestamp: Date.now(),
      ...additionalProps
    });
  } catch (error) {
    console.warn('Failed to track external game event:', error);
  }
}

/**
 * Gets the appropriate styling class for external games (Ultimate Experience styling)
 */
export function getExternalGameStyling(game: Game): string {
  if (!isExternalGame(game)) return '';
  
  // Ultimate Experience uses square aspect ratio vs rectangular for regular games
  return 'aspect-square'; // This will override default aspect-[4/3] for rectangular cards
}

/**
 * Handles the complete external game interaction flow
 */
export function handleExternalGameClick(game: Game): void {
  if (!isExternalGame(game)) {
    return;
  }

  openExternalGame(game);
}

export default {
  isExternalGame,
  getExternalGameUrl,
  validateExternalUrl,
  openExternalGame,
  handleExternalGameClick,
  getExternalGameStyling
};